import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import app from "flarum/forum/app";
import FormModal from "flarum/common/components/FormModal";
import Button from "flarum/common/components/Button";
import LoadingIndicator from "flarum/common/components/LoadingIndicator";
import Stream from "flarum/common/utils/Stream";
import withAttr from "flarum/common/utils/withAttr";
import chatState from "../state/chat";
import afterModalClosed from "../utils/afterModalClosed";
import EmojiPicker from "./EmojiPicker";
import { humanDuration } from "../utils/duration";
/**
 * Creates or edits a category channel.
 *
 * One component for both because the field set is identical — Discourse shows the
 * same form for "New channel" and the channel's settings tab. Only the title, the
 * submit label and whether the record already exists differ.
 *
 * Extends FormModal, not Modal: `Modal.wrapper()` returns a bare fragment, so a
 * `type="submit"` button inside it has no form and onsubmit never fires.
 */
export default class ChannelFormModal extends FormModal {
  constructor() {
    super(...arguments);
    _defineProperty(this, "name", void 0);
    _defineProperty(this, "description", void 0);
    _defineProperty(this, "emoji", void 0);
    _defineProperty(this, "uploadingImage", false);
    _defineProperty(this, "tagId", void 0);
    _defineProperty(this, "threading", void 0);
    _defineProperty(this, "slowMode", void 0);
    _defineProperty(this, "autoJoin", void 0);
    _defineProperty(this, "allowChannelWide", void 0);
    _defineProperty(this, "autoJoinOnReply", void 0);
    _defineProperty(this, "isPrivate", void 0);
    _defineProperty(this, "postPermission", void 0);
    _defineProperty(this, "postDiscussions", void 0);
  }
  oninit(vnode) {
    var _channel$name, _channel$description, _channel$emoji, _channel$slowModeSeco, _channel$postPermissi;
    super.oninit(vnode);
    const channel = this.attrs.channel;
    this.name = Stream((_channel$name = channel == null ? void 0 : channel.name()) != null ? _channel$name : "");
    this.description = Stream((_channel$description = channel == null ? void 0 : channel.description()) != null ? _channel$description : "");
    this.emoji = Stream((_channel$emoji = channel == null ? void 0 : channel.emoji()) != null ? _channel$emoji : "");
    this.tagId = Stream(channel != null && channel.tagId() ? String(channel.tagId()) : "");

    // A string, because it backs a <select> whose values are strings. Coerced
    // on submit rather than here, so an unchanged form round-trips exactly.
    this.slowMode = Stream(String((_channel$slowModeSeco = channel == null ? void 0 : channel.slowModeSeconds()) != null ? _channel$slowModeSeco : 0));
    this.threading = Stream(channel ? Boolean(channel.threadingEnabled()) : Boolean(app.forum.attribute("ramon-chat.threadingDefault")));
    this.autoJoin = Stream(channel ? Boolean(channel.autoJoin()) : false);
    this.allowChannelWide = Stream(channel ? channel.allowChannelWideMentions() !== false : true);
    this.autoJoinOnReply = Stream(channel ? Boolean(channel.autoJoinOnReply()) : false);

    // Public by default: a channel nobody can find is the surprising outcome, so
    // it has to be chosen rather than fallen into.
    this.isPrivate = Stream(channel ? Boolean(channel.isPrivate()) : false);
    this.postPermission = Stream((_channel$postPermissi = channel == null ? void 0 : channel.postPermission()) != null ? _channel$postPermissi : "all");
    this.postDiscussions = Stream(channel ? Boolean(channel.postDiscussions()) : false);
  }
  isEditing() {
    return Boolean(this.attrs.channel);
  }
  className() {
    return "ChatModal ChannelFormModal Modal--small";
  }
  title() {
    return app.translator.trans(this.isEditing() ? "ramon-chat.forum.edit_channel.title" : "ramon-chat.forum.browse.new_channel");
  }
  content() {
    var _app$session$user;
    return m("div", {
      className: "Modal-body"
    }, m("div", {
      className: "Form"
    }, m("div", {
      className: "Form-group"
    }, m("label", null, app.translator.trans("ramon-chat.forum.new_channel.name")), m("input", {
      className: "FormControl",
      type: "text",
      maxlength: 100,
      bidi: this.name,
      placeholder: app.translator.trans("ramon-chat.forum.new_channel.name_placeholder", {}, true),
      disabled: this.loading
    })), m("div", {
      className: "Form-group"
    }, m("label", null, app.translator.trans("ramon-chat.forum.new_channel.description")), m("textarea", {
      className: "FormControl",
      rows: 2,
      maxlength: 1000,
      bidi: this.description,
      disabled: this.loading
    })), m("div", {
      className: "Form-group"
    }, m("label", null, app.translator.trans("ramon-chat.forum.new_channel.emoji")), m(EmojiPicker, {
      value: this.emoji(),
      onchange: value => this.emoji(value != null ? value : ""),
      disabled: this.loading
    })), this.image(), this.visibility(), this.posting(), this.slowModeOptions(), this.tagOptions(), m("div", {
      className: "Form-group"
    }, m("label", {
      className: "checkbox"
    }, m("input", {
      type: "checkbox",
      checked: this.threading(),
      onchange: withAttr("checked", this.threading),
      disabled: this.loading
    }), app.translator.trans("ramon-chat.forum.info.threading")), m("label", {
      className: "checkbox"
    }, m("input", {
      type: "checkbox",
      checked: this.allowChannelWide(),
      onchange: withAttr("checked", this.allowChannelWide),
      disabled: this.loading
    }), app.translator.trans("ramon-chat.forum.settings.channel_wide_mentions")), m("label", {
      className: "checkbox"
    }, m("input", {
      type: "checkbox",
      checked: this.autoJoinOnReply(),
      onchange: withAttr("checked", this.autoJoinOnReply),
      disabled: this.loading
    }), app.translator.trans("ramon-chat.forum.info.auto_join_on_reply")), m("div", {
      className: "helpText"
    }, app.translator.trans("ramon-chat.forum.info.auto_join_on_reply_help")), m("label", {
      className: "checkbox"
    }, m("input", {
      type: "checkbox",
      checked: this.postDiscussions(),
      onchange: withAttr("checked", this.postDiscussions),
      disabled: this.loading
    }), app.translator.trans("ramon-chat.forum.info.post_discussions")), m("div", {
      className: "helpText"
    }, app.translator.trans("ramon-chat.forum.info.post_discussions_help")), ((_app$session$user = app.session.user) == null ? void 0 : _app$session$user.attribute("isAdmin")) !== false ? m('[', null, m("label", {
      className: "checkbox"
    }, m("input", {
      type: "checkbox",
      checked: this.autoJoin(),
      onchange: withAttr("checked", this.autoJoin),
      disabled: this.loading
    }), app.translator.trans("ramon-chat.forum.info.auto_join")), m("div", {
      className: "helpText"
    }, app.translator.trans("ramon-chat.forum.info.auto_join_help"))) : null), m("div", {
      className: "Form-group"
    }, m(Button, {
      className: "Button Button--primary Button--block",
      type: "submit",
      loading: this.loading,
      disabled: this.loading || this.name().trim() === ""
    }, app.translator.trans(this.isEditing() ? "ramon-chat.forum.edit_channel.submit" : "ramon-chat.forum.new_channel.submit"))), this.lifecycle()));
  }

  /**
   * Public or invitation-only.
   *
   * Radios rather than a checkbox: the two options are named, and "private" is a
   * decision about who can find the channel at all — not a toggle whose unchecked
   * state should be inferred from its label.
   */
  /**
   * A picture for the channel, instead of its emoji.
   *
   * Only offered once the channel exists: the upload is addressed to a channel id,
   * so on the create form there is nothing to attach it to yet. Setting the emoji
   * now and the picture after saving is a smaller surprise than a control that
   * silently does nothing on first use.
   */
  image() {
    const channel = this.attrs.channel;
    if (!channel) return null;
    const url = channel.imageUrl();
    return m("div", {
      className: "Form-group ChatChannelForm-image"
    }, m("label", null, app.translator.trans("ramon-chat.forum.new_channel.image")), m("div", {
      className: "helpText"
    }, app.translator.trans("ramon-chat.forum.new_channel.image_help")), m("div", {
      className: "ChatChannelForm-imageRow"
    }, m("div", {
      className: "ChatChannelForm-imagePreview"
    }, url ? m("img", {
      src: url,
      alt: ""
    }) : m("i", {
      className: "fas fa-hashtag",
      "aria-hidden": "true"
    })), this.uploadingImage ? m(LoadingIndicator, {
      display: "inline",
      size: "small"
    }) : m('[', null, m("label", {
      className: "Button"
    }, app.translator.trans("ramon-chat.forum.new_channel.image_upload"), m("input", {
      type: "file",
      accept: "image/*",
      style: {
        display: "none"
      },
      onchange: e => this.uploadImage(e)
    })), url ? m(Button, {
      className: "Button",
      onclick: () => this.clearImage()
    }, app.translator.trans("ramon-chat.forum.new_channel.image_remove")) : null)));
  }
  async uploadImage(e) {
    var _input$files;
    const input = e.target;
    const file = (_input$files = input.files) == null ? void 0 : _input$files[0];
    const channel = this.attrs.channel;
    if (!file || !channel) return;
    this.uploadingImage = true;
    m.redraw();
    const body = new FormData();
    body.append("image", file);
    try {
      var _response$data$imageU, _response$data;
      const response = await app.request({
        method: "POST",
        url: "".concat(app.forum.attribute("apiUrl"), "/chat/channels/").concat(channel.id(), "/image"),
        // The default serializer would JSON-encode the FormData into "[object
        // FormData]" and the request would arrive with no file at all.
        serialize: raw => raw,
        body
      });
      channel.pushAttributes({
        imageUrl: (_response$data$imageU = response == null || (_response$data = response.data) == null ? void 0 : _response$data.imageUrl) != null ? _response$data$imageU : null
      });
    } catch (err) {
      var _err$response$errors$, _err$response;
      app.alerts.show({
        type: "error"
      }, (_err$response$errors$ = err == null || (_err$response = err.response) == null || (_err$response = _err$response.errors) == null || (_err$response = _err$response[0]) == null ? void 0 : _err$response.detail) != null ? _err$response$errors$ : app.translator.trans("ramon-chat.forum.new_channel.image_failed"));
    } finally {
      this.uploadingImage = false;
      // Cleared so re-picking the same file still fires a change event.
      input.value = "";
      m.redraw();
    }
  }
  async clearImage() {
    const channel = this.attrs.channel;
    if (!channel) return;
    this.uploadingImage = true;
    m.redraw();
    try {
      await app.request({
        method: "DELETE",
        url: "".concat(app.forum.attribute("apiUrl"), "/chat/channels/").concat(channel.id(), "/image")
      });
      channel.pushAttributes({
        imageUrl: null
      });
    } catch (_unused) {
      app.alerts.show({
        type: "error"
      }, app.translator.trans("ramon-chat.forum.new_channel.image_failed"));
    } finally {
      this.uploadingImage = false;
      m.redraw();
    }
  }
  visibility() {
    return m("div", {
      className: "Form-group"
    }, m("label", null, app.translator.trans("ramon-chat.forum.new_channel.visibility")), m("label", {
      className: "checkbox"
    }, m("input", {
      type: "radio",
      name: "ramon-chat-visibility",
      checked: !this.isPrivate(),
      onchange: () => this.isPrivate(false),
      disabled: this.loading
    }), app.translator.trans("ramon-chat.forum.new_channel.public")), m("div", {
      className: "helpText"
    }, app.translator.trans("ramon-chat.forum.new_channel.public_help")), m("label", {
      className: "checkbox"
    }, m("input", {
      type: "radio",
      name: "ramon-chat-visibility",
      checked: this.isPrivate(),
      onchange: () => this.isPrivate(true),
      disabled: this.loading
    }), app.translator.trans("ramon-chat.forum.new_channel.private")), m("div", {
      className: "helpText"
    }, app.translator.trans("ramon-chat.forum.new_channel.private_help")));
  }

  /**
   * Who may post.
   *
   * A separate question from visibility: "who can see this" and "who can write in
   * it" are independent, and a private channel only moderators post in is a
   * perfectly ordinary announcement channel for an invited audience.
   */
  posting() {
    return m("div", {
      className: "Form-group"
    }, m("label", null, app.translator.trans("ramon-chat.forum.new_channel.post_permission")), m("select", {
      className: "FormControl",
      value: this.postPermission(),
      onchange: withAttr("value", this.postPermission),
      disabled: this.loading
    }, m("option", {
      value: "all"
    }, app.translator.trans("ramon-chat.forum.new_channel.post_all", {}, true)), m("option", {
      value: "moderators"
    }, app.translator.trans("ramon-chat.forum.new_channel.post_moderators", {}, true))), m("div", {
      className: "helpText"
    }, app.translator.trans("ramon-chat.forum.new_channel.post_permission_help")));
  }

  /**
   * Close and archive, on an existing channel only.
   *
   * Neither means anything for a channel that does not exist yet, and both act
   * immediately rather than on submit — they are not settings being edited, they
   * are state changes, and mixing them into the form's save would make an
   * unsaved-and-abandoned form able to archive something.
   */
  lifecycle() {
    const channel = this.attrs.channel;
    if (!channel) return null;
    const closed = channel.status() === "closed";
    const archived = Boolean(channel.archivedAt());
    const items = [];
    if (channel.canClose() && !archived) {
      items.push(m(Button, {
        className: "Button",
        icon: closed ? "fas fa-lock-open" : "fas fa-lock",
        loading: this.loading,
        onclick: () => this.setStatus(closed ? "open" : "closed")
      }, app.translator.trans(closed ? "ramon-chat.forum.info.reopen_channel" : "ramon-chat.forum.info.close_channel")));
    }
    if (channel.canArchive() && !archived) {
      items.push(m(Button, {
        className: "Button",
        icon: "fas fa-box-archive",
        loading: this.loading,
        onclick: () => this.archive()
      }, app.translator.trans("ramon-chat.forum.info.archive_channel")));
    }
    if (items.length === 0) return null;
    return m("div", {
      className: "Form-group ChannelFormModal-lifecycle"
    }, m("label", null, app.translator.trans("ramon-chat.forum.edit_channel.lifecycle")), m("div", {
      className: "ChannelFormModal-lifecycleActions"
    }, items), m("div", {
      className: "helpText"
    }, app.translator.trans("ramon-chat.forum.edit_channel.lifecycle_help")));
  }
  async setStatus(status) {
    await this.act("/chat-channels/".concat(this.attrs.channel.id(), "/status"), {
      status
    });
  }
  async archive() {
    if (!confirm(app.translator.trans("ramon-chat.forum.edit_channel.archive_confirm", {}, true))) return;
    await this.act("/chat-channels/".concat(this.attrs.channel.id(), "/archive"), {});
  }
  async act(path, attributes) {
    this.loading = true;
    m.redraw();
    try {
      const payload = await app.request({
        method: "POST",
        url: "".concat(app.forum.attribute("apiUrl")).concat(path),
        body: {
          data: {
            attributes
          }
        }
      });
      if (payload != null && payload.data) app.store.pushPayload(payload);
      this.hide();

      // Same reason as the save path above: whatever the caller does next must not
      // race the close animation.
      afterModalClosed(() => {
        var _this$attrs$onSaved, _this$attrs;
        return (_this$attrs$onSaved = (_this$attrs = this.attrs).onSaved) == null ? void 0 : _this$attrs$onSaved.call(_this$attrs, this.attrs.channel);
      });
    } catch (e) {
      var _e$response$errors$0$, _e$response;
      app.alerts.show({
        type: "error"
      }, (_e$response$errors$0$ = e == null || (_e$response = e.response) == null || (_e$response = _e$response.errors) == null || (_e$response = _e$response[0]) == null ? void 0 : _e$response.detail) != null ? _e$response$errors$0$ : app.translator.trans("ramon-chat.forum.edit_channel.failed"));
    } finally {
      this.loading = false;
      m.redraw();
    }
  }

  /**
   * Category picker, rendered only when flarum/tags is present. A tag-bound
   * channel inherits that tag's permissions, which is how a restricted category
   * yields a restricted channel.
   */
  tagOptions() {
    var _flarum$extensions;
    const tags = app.store.all("tags");
    if (!("flarum-tags" in ((_flarum$extensions = flarum.extensions) != null ? _flarum$extensions : {})) || tags.length === 0) return null;
    return m("div", {
      className: "Form-group"
    }, m("label", null, app.translator.trans("ramon-chat.forum.new_channel.category")), m("select", {
      className: "FormControl",
      value: this.tagId(),
      onchange: withAttr("value", this.tagId),
      disabled: this.loading
    }, m("option", {
      value: ""
    }, app.translator.trans("ramon-chat.forum.new_channel.no_category")), tags.filter(tag => {
      var _tag$isChild;
      return !((_tag$isChild = tag.isChild) != null && _tag$isChild.call(tag));
    }).map(tag => m("option", {
      key: tag.id(),
      value: String(tag.id())
    }, tag.name()))), m("div", {
      className: "helpText"
    }, app.translator.trans("ramon-chat.forum.new_channel.category_help")));
  }

  /**
   * Nothing awaits this method, so a rejection here would surface as an unhandled
   * promise rejection rather than as feedback. Every failure path is handled inline.
   */
  /**
   * Slow mode: how long each person waits between messages here.
   *
   * A fixed list rather than a free number field. The useful values are few and
   * far apart — five seconds calms a room, five minutes changes what the room is
   * for — and a text box invites 7s, which is nobody's intention.
   *
   * Moderators are exempt, and the help text says so: someone enabling this needs
   * to know it will not throttle them out of their own moderation.
   */
  slowModeOptions() {
    const steps = [0, 5, 10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600, 21600];
    return m("div", {
      className: "Form-group"
    }, m("label", null, app.translator.trans("ramon-chat.forum.new_channel.slow_mode")), m("select", {
      className: "FormControl",
      value: this.slowMode(),
      onchange: withAttr("value", this.slowMode),
      disabled: this.loading
    }, steps.map(seconds => m("option", {
      key: seconds,
      value: String(seconds)
    }, seconds === 0 ? app.translator.trans("ramon-chat.forum.new_channel.slow_mode_off", {}, true) : humanDuration(seconds)))), m("p", {
      className: "helpText"
    }, app.translator.trans("ramon-chat.forum.new_channel.slow_mode_help")));
  }
  onsubmit(e) {
    var _this$attrs$channel;
    e.preventDefault();
    if (this.loading) return;
    this.loading = true;
    const editing = this.isEditing();
    const attributes = {
      name: this.name().trim(),
      description: this.description().trim() || null,
      emoji: this.emoji().trim() || null,
      threadingEnabled: this.threading(),
      slowModeSeconds: Number(this.slowMode()) || 0,
      allowChannelWideMentions: this.allowChannelWide(),
      autoJoin: this.autoJoin(),
      autoJoinOnReply: this.autoJoinOnReply(),
      isPrivate: this.isPrivate(),
      postPermission: this.postPermission(),
      postDiscussions: this.postDiscussions(),
      tagId: this.tagId() ? Number(this.tagId()) : null
    };

    // `type` is writable only on create; sending it on update would be rejected.
    if (!editing) {
      attributes.type = "category";
    }
    const record = (_this$attrs$channel = this.attrs.channel) != null ? _this$attrs$channel : app.store.createRecord("chat-channels");
    record.save(attributes).then(channel => {
      if (!editing && !chatState.channels.some(c => c.id() === channel.id())) {
        // The server subscribes the creator, so the channel belongs in the
        // sidebar straight away rather than after the next poll.
        chatState.channels.unshift(channel);
      }
      this.hide();
      if (editing) {
        app.alerts.show({
          type: "success"
        }, app.translator.trans("ramon-chat.forum.edit_channel.saved"));
      }

      // Deferred until the modal has actually gone.
      //
      // Core's ModalManager.animateHide() only clears its `modalClosing` latch
      // from a `transitionend` handler. A caller that changes the route in this
      // same tick can interrupt that transition, the event never fires, and the
      // latch stays set — after which every later animateHide() returns
      // immediately and no modal can be closed again until the page is reloaded.
      // That is why creating a channel left the next modal stuck with a dead X.
      afterModalClosed(() => {
        var _this$attrs$onSaved2, _this$attrs2;
        return (_this$attrs$onSaved2 = (_this$attrs2 = this.attrs).onSaved) == null ? void 0 : _this$attrs$onSaved2.call(_this$attrs2, channel);
      });
    }).catch(error => {
      this.loading = false;

      // FormModal.onerror renders validation errors above the form and refocuses
      // the offending field; anything else gets an alert so it is never silent.
      if (error != null && error.alert) {
        this.onerror(error);
      } else {
        var _error$response$error, _error$response;
        app.alerts.show({
          type: "error"
        }, (_error$response$error = error == null || (_error$response = error.response) == null || (_error$response = _error$response.errors) == null || (_error$response = _error$response[0]) == null ? void 0 : _error$response.detail) != null ? _error$response$error : app.translator.trans(editing ? "ramon-chat.forum.edit_channel.failed" : "ramon-chat.forum.new_channel.failed"));
        m.redraw();
      }
    });
  }
}
flarum.reg.add('ramon-chat', 'forum/components/ChannelFormModal', ChannelFormModal);